import { useState, useEffect } from "react";
import { Loader, CheckCircle, XCircle, Camera, Search, HardHat, Undo2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { fmt } from "../lib/utils";
import Avatar from "./common/Avatar";
import Button from "./common/Button";
import SectionLabel from "./common/SectionLabel";
import StatusDot from "./common/StatusDot";

function WorkPhotoThumb({ projectId, contractId, wcId, photoId, onClick, onDelete }: any) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/projects/${projectId}/contracts/${contractId}/work-completions/${wcId}/photos/${photoId}`, { responseType: "blob" })
      .then(res => { if (!cancelled) setUrl(URL.createObjectURL(new Blob([res.data]))); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoId]);
  return (
    <div className="relative w-14 h-14 rounded-[10px] overflow-hidden bg-black/[0.04] group">
      {url ? (
        <img src={url} alt="" className="w-full h-full object-cover cursor-pointer" onClick={onClick} />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><Loader className="w-3 h-3 animate-spin text-faint" /></div>
      )}
      {onDelete && !photoId.startsWith("legacy-") && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
}

export default function WorkApprovalView() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [workStatus, setWorkStatus] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [comment, setComment] = useState<Record<string, string>>({});
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await api.get("/projects/");
      setProjects(res.data.projects);
    } catch {}
    finally { setLoading(false); }
  };

  const fetchWorkStatus = async (projectId: string, contractId: string) => {
    try {
      const res = await api.get(`/projects/${projectId}/contracts/${contractId}/work-status`);
      setWorkStatus(res.data);
      setSelectedContractId(contractId);
      setSelectedProjectId(projectId);
    } catch {}
  };

  const submitCompletion = async (linjeNr: number, status: "completed" | "not_completed") => {
    if (!selectedProjectId || !selectedContractId) return;
    const key = `${selectedContractId}-${linjeNr}`;
    setActionLoading(key);
    try {
      await api.post(`/projects/${selectedProjectId}/contracts/${selectedContractId}/work-completions/${linjeNr}`, {
        status, comment: comment[key] || undefined
      });
      fetchWorkStatus(selectedProjectId, selectedContractId);
      setComment(prev => ({ ...prev, [key]: "" }));
    } catch {}
    finally { setActionLoading(null); }
  };

  const undoCompletion = async (linjeNr: number) => {
    if (!selectedProjectId || !selectedContractId) return;
    setActionLoading(`${selectedContractId}-${linjeNr}`);
    try {
      await api.delete(`/projects/${selectedProjectId}/contracts/${selectedContractId}/work-completions/${linjeNr}`);
      fetchWorkStatus(selectedProjectId, selectedContractId);
    } catch {}
    finally { setActionLoading(null); }
  };

  const uploadPhotos = async (linjeNr: number, files: FileList) => {
    if (!selectedProjectId || !selectedContractId) return;
    for (const file of Array.from(files)) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        await api.post(`/projects/${selectedProjectId}/contracts/${selectedContractId}/work-completions/${linjeNr}/photo`, formData);
      } catch {}
    }
    fetchWorkStatus(selectedProjectId, selectedContractId);
  };

  const viewPhoto = async (wcId: string, photoId: string) => {
    if (!selectedProjectId || !selectedContractId) return;
    try {
      const res = await api.get(`/projects/${selectedProjectId}/contracts/${selectedContractId}/work-completions/${wcId}/photos/${photoId}`, { responseType: "blob" });
      setPhotoPreview(URL.createObjectURL(new Blob([res.data])));
    } catch { }
  };

  const deletePhoto = async (wcId: string, photoId: string) => {
    if (!selectedProjectId || !selectedContractId) return;
    if (photoId.startsWith("legacy-")) return;
    try {
      await api.delete(`/projects/${selectedProjectId}/contracts/${selectedContractId}/work-completions/${wcId}/photos/${photoId}`);
      fetchWorkStatus(selectedProjectId, selectedContractId);
    } catch {}
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.vendors || []).some((v: any) => (v.vendor_name || v.name || "").toLowerCase().includes(search.toLowerCase()))
  );

  const completedCount = workStatus?.completed || 0;
  const pendingCount = workStatus?.pending || 0;
  const notCompletedCount = workStatus?.not_completed || 0;
  const totalPosts = workStatus?.total_posts || 0;
  const progressPct = totalPosts > 0 ? Math.round(completedCount / totalPosts * 100) : 0;

  if (loading && !projects.length) return (
    <div className="flex flex-col items-center justify-center py-32 gap-3">
      <Loader className="w-6 h-6 animate-spin text-info" />
      <span className="text-[13px] text-faint">{t("work.laster")}</span>
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-52px)] -mx-4 sm:-mx-6 md:-mx-8 -my-5 sm:-my-7 md:-my-9">
      {/* Lightbox */}
      {photoPreview && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => { URL.revokeObjectURL(photoPreview); setPhotoPreview(null); }}>
          <img src={photoPreview} className="max-w-full max-h-full rounded-lg" alt="Arbeidsdokumentasjon" onClick={e => e.stopPropagation()} />
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white text-night flex items-center justify-center"
            onClick={() => { URL.revokeObjectURL(photoPreview); setPhotoPreview(null); }}>
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ── VENSTRE SIDEBAR ── */}
      <aside className={`${workStatus ? "hidden md:flex" : "flex"} w-full md:w-[280px] shrink-0 border-r border-line flex-col overflow-hidden`}>
        <div className="px-5 pt-8 pb-3">
          <h1 className="text-[28px] font-extrabold text-night leading-none tracking-tight mb-1">{t("work.godkjenning")}</h1>
          <p className="text-[12px] text-muted font-semibold">{t("work.bekreft_utfort")}</p>
        </div>

        {/* Mini KPI 2x2 */}
        {workStatus && (
          <div className="mx-5 mb-3 grid grid-cols-2 gap-0 border-t border-line border-b border-line">
            <div className="px-3 py-3">
              <p className="text-label uppercase text-faint mb-1">{t("work.bekreftet")}</p>
              <p className="text-[22px] font-extrabold text-pos tabular leading-none">{completedCount}</p>
            </div>
            <div className="px-3 py-3 border-l border-line">
              <p className="text-label uppercase text-faint mb-1">{t("work.venter")}</p>
              <p className="text-[22px] font-extrabold text-flag tabular leading-none">{pendingCount}</p>
            </div>
            <div className="px-3 py-3 border-t border-line">
              <p className="text-label uppercase text-faint mb-1">{t("work.avvist")}</p>
              <p className="text-[22px] font-extrabold text-neg tabular leading-none">{notCompletedCount}</p>
            </div>
            <div className="px-3 py-3 border-t border-line border-l border-line">
              <p className="text-label uppercase text-faint mb-1">{t("work.progresjon")}</p>
              <p className="text-[22px] font-extrabold text-info tabular leading-none">{progressPct}%</p>
            </div>
          </div>
        )}

        {/* Søk */}
        <div className="px-5 pb-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-faint absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t("common.sok")}
              className="w-full pl-8 pr-3 py-1.5 bg-black/[0.05] rounded-[8px] text-[13px] text-night placeholder:text-faint focus:bg-white focus:ring-[3px] focus:ring-night/10 focus:outline-none transition-all" />
          </div>
        </div>

        {/* Prosjekt-tre */}
        <div className="flex-1 overflow-y-auto border-t border-line">
          {filteredProjects.length === 0 ? (
            <p className="text-[13px] text-faint text-center py-10 px-5">{t("work.ingen_prosjekter")}</p>
          ) : filteredProjects.map(p => (
            <div key={p.id} className="border-b border-line">
              <div className="px-5 pt-3 pb-1">
                <p className="text-[10.5px] font-bold uppercase text-faint tracking-wide">{p.name}</p>
              </div>
              {(p.vendors || []).map((v: any) => {
                const cid = v.contract_id || v.id;
                const isSelected = selectedContractId === cid;
                return (
                  <button key={cid} onClick={() => fetchWorkStatus(p.id, cid)}
                    className={`w-full text-left px-5 py-2.5 flex items-center gap-2.5 transition-colors ${
                      isSelected ? "bg-info/[0.08]" : "hover:bg-black/[0.02]"
                    }`}>
                    <Avatar name={v.vendor_name || v.name || "?"} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] truncate ${isSelected ? "font-bold text-info" : "font-semibold text-night"}`}>
                        {v.vendor_name || v.name}
                      </p>
                      {v.contract_ref && <p className="text-[11px] text-faint truncate tabular">{v.contract_ref}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </aside>

      {/* ── HØYRE PANEL ── */}
      <main className={`${workStatus ? "flex" : "hidden md:flex"} flex-1 overflow-y-auto bg-card min-w-0 flex-col`}>
        {!workStatus ? (
          <div className="h-full flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-2xl bg-black/[0.04] flex items-center justify-center mb-4">
              <HardHat className="w-7 h-7 text-faint" />
            </div>
            <p className="text-[15px] font-bold text-night mb-1">{t("work.velg_leverandor")}</p>
            <p className="text-[13px] text-faint">{t("work.velg_fra_listen")}</p>
          </div>
        ) : (
          <div className="px-4 sm:px-6 md:px-8 pt-5 md:pt-9 pb-12 animate-fade-up" key={selectedContractId}>
            <button onClick={() => setWorkStatus(null)} className="md:hidden mb-3 inline-flex items-center gap-1 text-[13px] font-semibold text-info -ml-1 px-2 py-1.5 rounded-[8px] active:bg-info/10">
              {t("common.tilbake")}
            </button>
            {/* Vendor hero */}
            <div className="flex items-center gap-3 sm:gap-4 mb-5 sm:mb-6">
              <Avatar name={workStatus.vendor_name || "?"} size="xl" />
              <div className="flex-1 min-w-0">
                <h2 className="text-[20px] sm:text-[24px] font-extrabold text-night leading-tight break-words">{workStatus.vendor_name}</h2>
                <p className="text-[12.5px] sm:text-[13px] text-muted mt-1 tabular">{t("work.poster_bekreftet", { completed: completedCount, total: totalPosts })}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[26px] sm:text-[34px] font-extrabold text-info tabular leading-none">{progressPct}%</p>
              </div>
            </div>

            {/* Progressbar */}
            <div className="h-1.5 rounded-full bg-black/[0.06] overflow-hidden mb-8">
              <div className="h-full bg-pos transition-all duration-700 ease-out rounded-full" style={{ width: `${progressPct}%` }} />
            </div>

            <SectionLabel className="!mt-0">{t("work.kontraktsposter", { count: totalPosts })}</SectionLabel>
            <div>
              {workStatus.line_items?.map((li: any, i: number) => {
                const postNr = li.post_nr || i + 1;
                const completion = workStatus.completions?.find((c: any) => Math.round(c.linje_nr) === postNr);
                const key = `${selectedContractId}-${postNr}`;
                const isCompleted = completion?.status === "completed";
                const isNotCompleted = completion?.status === "not_completed";

                const circleCls = isCompleted ? "bg-pos text-white"
                  : isNotCompleted ? "bg-neg text-white"
                  : "bg-black/[0.06] text-muted";

                return (
                  <div key={postNr} className={`border-b border-line py-4 ${
                    isCompleted ? "bg-pos/[0.04]" : isNotCompleted ? "bg-neg/[0.04]" : ""
                  }`}>
                    <div className="flex items-start gap-4">
                      {/* Circle nr */}
                      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold tabular ${circleCls}`}>
                        {isCompleted ? <CheckCircle className="w-4 h-4" /> : isNotCompleted ? <XCircle className="w-4 h-4" /> : postNr}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <p className="text-[14px] font-bold text-night leading-snug min-w-0 flex-1">{li.description}</p>
                          {!completion && (
                            <span className="flex items-center gap-1.5 shrink-0 text-[10.5px] font-bold uppercase text-flag">
                              <StatusDot color="amb" size="sm" /> {t("work.venter_bekreftelse")}
                            </span>
                          )}
                          {isCompleted && (
                            <span className="text-[10.5px] font-bold uppercase text-pos shrink-0">{t("work.bekreftet")}</span>
                          )}
                          {isNotCompleted && (
                            <span className="text-[10.5px] font-bold uppercase text-neg shrink-0">{t("work.ikke_utfort")}</span>
                          )}
                        </div>
                        {li.quantity != null && li.unit_price != null && (
                          <p className="text-[12px] text-muted tabular mt-0.5">
                            {li.quantity} × {fmt(li.unit_price, workStatus.currency || "NOK")} = <span className="font-bold text-night">{fmt(li.quantity * li.unit_price, workStatus.currency || "NOK")}</span>
                          </p>
                        )}

                        {/* Completion meta */}
                        {completion && (
                          <div className="mt-2">
                            <p className="text-[11.5px] text-muted tabular">
                              {completion.confirmed_by_name} · {completion.confirmed_at?.slice(0, 10)}
                            </p>
                            {completion.comment && <p className="text-[12px] text-muted italic mt-0.5">"{completion.comment}"</p>}
                            {completion.photo_ids && completion.photo_ids.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {completion.photo_ids.map((pid: string) => (
                                  <WorkPhotoThumb key={pid} projectId={selectedProjectId} contractId={selectedContractId}
                                    wcId={completion.id} photoId={pid}
                                    onClick={() => viewPhoto(completion.id, pid)}
                                    onDelete={() => deletePhoto(completion.id, pid)} />
                                ))}
                              </div>
                            )}
                            <div className="flex gap-2 mt-3">
                              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-[11.5px] font-semibold bg-black/[0.05] text-night cursor-pointer hover:bg-black/[0.08] transition-colors">
                                <Camera className="w-3.5 h-3.5" /> {t("work.legg_til_bilde")}
                                <input type="file" accept="image/*" multiple className="hidden"
                                  onChange={e => e.target.files && uploadPhotos(postNr, e.target.files)} />
                              </label>
                              <button onClick={() => undoCompletion(postNr)}
                                disabled={actionLoading === key}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-[11.5px] font-semibold text-muted hover:text-neg hover:bg-neg/[0.08] transition-colors">
                                <Undo2 className="w-3.5 h-3.5" /> {t("common.angre")}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Handlinger for ubekreftede / avviste */}
                        {(!completion || isNotCompleted) && (
                          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                            <input value={comment[key] || ""} onChange={e => setComment(prev => ({ ...prev, [key]: e.target.value }))}
                              placeholder={t("work.kommentar_valgfritt")}
                              className="field-ios !py-2 text-[13px] flex-1 w-full" />
                            <div className="flex gap-2">
                              <Button variant="success" loading={actionLoading === key} icon={<CheckCircle className="w-4 h-4" />}
                                onClick={() => submitCompletion(postNr, "completed")}>
                                {t("work.utfort")}
                              </Button>
                              {!completion && (
                                <Button variant="danger" icon={<XCircle className="w-4 h-4" />} disabled={actionLoading === key}
                                  onClick={() => submitCompletion(postNr, "not_completed")}>
                                  {t("common.nei")}
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
